import { getFluidTemperatureLimits, getIsothermEnthalpy, getSaturationPoint } from '~/helpers/coolPropPro';

export type PHPoint = {
  h: number;
  p: number;
  point?: string;
  label?: string;
};

export type PHCycleResult = {
  refrigerante: string;
  entalpia_h1: number;
  entalpia_h2_real: number;
  entalpia_h3: number;
  entalpia_h4: number;
  P1: number;
  P2: number;
  P3: number;
  P4: number;
};

export type PHZoomParams = {
  h_min: number;
  h_max: number;
  p_min: number;
  p_max: number;
};

export type PHIsothermSeries = {
  name: string;
  points: PHPoint[];
  labelPoint: PHPoint | null;
};

export type PHDiagramData = {
  saturationLiquid: PHPoint[];
  saturationVapor: PHPoint[];
  cycle: PHPoint[];
  isotherms: PHIsothermSeries[];
  xDomain: [number, number];
  yDomain: [number, number];
  yTicks: number[];
};

const DEFAULT_TEMP_RANGE_K: [number, number] = [173.15, 373.15];
const TEMPERATURES_ISOTHERM_C = [-45, -25, -5, 15, 35, 55, 75, 95];

const isFiniteNumber = (value: number): boolean => Number.isFinite(value);
const isPositiveNumber = (value: number): boolean => Number.isFinite(value) && value > 0;

const celsiusToFahrenheit = (celsius: number): number => (celsius * 9) / 5 + 32;

const linspace = (start: number, end: number, count: number): number[] => {
  if (count <= 1) {
    return [start];
  }

  const step = (end - start) / (count - 1);
  return Array.from({ length: count }, (_, index) => start + step * index);
};

const logspace = (startExp: number, endExp: number, count: number): number[] => {
  if (count <= 1) {
    return [10 ** startExp];
  }

  const step = (endExp - startExp) / (count - 1);
  return Array.from({ length: count }, (_, index) => 10 ** (startExp + step * index));
};

const uniqueSortedIntegers = (values: number[]): number[] =>
  Array.from(new Set(values.map((value) => Math.round(value)).filter((value) => value >= 1))).sort((a, b) => a - b);

const buildDefaultDomains = (
  allEnthalpies: number[],
  allPressures: number[],
): { xDomain: [number, number]; yDomain: [number, number] } => {
  const minH = allEnthalpies.length ? Math.min(...allEnthalpies) : 100;
  const maxH = allEnthalpies.length ? Math.max(...allEnthalpies) : 500;
  const minP = allPressures.length ? Math.min(...allPressures) : 0.5;
  const maxP = allPressures.length ? Math.max(...allPressures) : 40;

  return {
    xDomain: [
      Math.floor((minH - 20) / 10) * 10,
      Math.ceil((maxH + 20) / 10) * 10,
    ],
    yDomain: [Math.max(0.5, minP * 0.8), maxP * 1.2],
  };
};

const buildIsothermSeries = async (
  refrigerante: string,
  pressureGrid: number[],
  tempC: number,
  tempUnit: '°C' | '°F',
): Promise<PHIsothermSeries | null> => {
  const tempK = tempC + 273.15;
  const enthalpyValues = await Promise.all(
    pressureGrid.map((pBar) => getIsothermEnthalpy(pBar, tempK, refrigerante)),
  );

  const points = pressureGrid
    .map((pBar, index) => ({
      p: pBar,
      h: enthalpyValues[index],
    }))
    .filter((item) => (
      item.h !== null &&
      item.h !== undefined &&
      isPositiveNumber(item.p) &&
      isFiniteNumber(item.h)
    ))
    .map((item) => ({
      h: item.h,
      p: item.p,
    }));

  if (!points.length) {
    return null;
  }

  let labelAnchor = points[0];

  points.forEach((point) => {
    if (point.h > labelAnchor.h) {
      labelAnchor = point;
    }
  });

  const labelValue = tempUnit === '°F' ? celsiusToFahrenheit(tempC) : tempC;

  return {
    name: `isotherm-${tempC}`,
    points,
    labelPoint: {
      h: labelAnchor.h,
      p: labelAnchor.p,
      label: `${labelValue.toFixed(0)}${tempUnit}`,
    },
  };
};

export async function generatePHDiagramData(
  resultados: PHCycleResult,
  tempUnit: '°C' | '°F' = '°C',
  zoomParams?: PHZoomParams,
): Promise<PHDiagramData> {
  const limits = await getFluidTemperatureLimits(resultados.refrigerante);
  const [startTemp, endTemp] = limits ? [limits.Ttriple, limits.Tcrit] : DEFAULT_TEMP_RANGE_K;
  const temperatureGrid = linspace(startTemp, endTemp, 180);

  const saturationPoints = await Promise.all(
    temperatureGrid.map((temperatureK) => getSaturationPoint(temperatureK, resultados.refrigerante)),
  );

  const validSaturation = saturationPoints
    .filter((point) => point !== null && point !== undefined)
    .filter((point) => isPositiveNumber(point.p))
    .filter((point) => isFiniteNumber(point.hLiq))
    .filter((point) => isFiniteNumber(point.hVap));

  const saturationLiquid = validSaturation.map((point) => ({ h: point.hLiq, p: point.p }));
  const saturationVapor = validSaturation.map((point) => ({ h: point.hVap, p: point.p }));

  const cycle: PHPoint[] = [
    { h: resultados.entalpia_h1, p: resultados.P1 / 100000, point: '1' },
    { h: resultados.entalpia_h2_real, p: resultados.P2 / 100000, point: '2' },
    { h: resultados.entalpia_h3, p: resultados.P3 / 100000, point: '3' },
    { h: resultados.entalpia_h4, p: resultados.P4 / 100000, point: '4' },
    { h: resultados.entalpia_h1, p: resultados.P1 / 100000, point: '1' },
  ].filter((point) => isFiniteNumber(point.h) && isPositiveNumber(point.p));

  const basePressures = [...saturationLiquid, ...saturationVapor, ...cycle]
    .map((point) => point.p)
    .filter(isPositiveNumber);

  const minPressure = Math.max(0.1, (basePressures.length ? Math.min(...basePressures) : 0.1) * 0.5);
  const maxPressure = (basePressures.length ? Math.max(...basePressures) : 100) * 1.5;
  const pressureGrid = logspace(Math.log10(minPressure), Math.log10(maxPressure), 320);

  const isothermCandidates = await Promise.all(
    TEMPERATURES_ISOTHERM_C.map((tempC) => buildIsothermSeries(resultados.refrigerante, pressureGrid, tempC, tempUnit)),
  );

  const isotherms: PHIsothermSeries[] = [];

  isothermCandidates.forEach((series) => {
    if (series !== null && series !== undefined) {
      isotherms.push(series);
    }
  });

  const allEnthalpies = [
    ...saturationLiquid.map((point) => point.h),
    ...saturationVapor.map((point) => point.h),
    ...cycle.map((point) => point.h),
    ...isotherms.flatMap((series) => series.points.map((point) => point.h)),
  ].filter(isFiniteNumber);

  const allPressures = [
    ...basePressures,
    ...isotherms.flatMap((series) => series.points.map((point) => point.p)),
  ].filter(isPositiveNumber);

  let domains: { xDomain: [number, number]; yDomain: [number, number] };

  if (zoomParams) {
    domains = {
      xDomain: [zoomParams.h_min, zoomParams.h_max],
      yDomain: [zoomParams.p_min, zoomParams.p_max],
    };
  } else {
    domains = buildDefaultDomains(allEnthalpies, allPressures);
  }

  const yTicks =
    domains.yDomain[0] > 0 && domains.yDomain[1] > domains.yDomain[0]
      ? uniqueSortedIntegers(logspace(Math.log10(domains.yDomain[0]), Math.log10(domains.yDomain[1]), 10))
      : [];

  return {
    saturationLiquid,
    saturationVapor,
    cycle,
    isotherms,
    xDomain: domains.xDomain,
    yDomain: domains.yDomain,
    yTicks,
  };
}
